// src/features/websites/components/RssConfigFields.tsx
import React, { useMemo, useState } from 'react';
import {
  Paper,
  TextField,
  Typography,
  Box,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  CircularProgress,
  Divider,
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Article as ArticleIcon, Check as CheckIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { importArticlesFromRss, ImportFromRssResponse } from '../../articles/api';
import { useDispatch } from 'react-redux';
import { fetchFilteredArticles } from '../../articles/slices/articlesSlice';
import { AppDispatch } from '../../../store';

interface RssConfigFieldsProps {
  websitePK: string;
  rssUrl: string;
  error?: string;
  isLoading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const RssConfigFields: React.FC<RssConfigFieldsProps> = ({
  websitePK,
  rssUrl,
  error,
  isLoading,
  onChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Optional parameters
  const [maxItems, setMaxItems] = useState<number | ''>(10);
  const [onlyNew, setOnlyNew] = useState<boolean>(true);
  const [dryRun, setDryRun] = useState<boolean>(true);

  // Local state
  const [localLoading, setLocalLoading] = useState(false);
  const [results, setResults] = useState<ImportFromRssResponse | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [localErrors, setLocalErrors] = useState<{ rssUrl?: string; maxItems?: string }>({});

  const isBusy = isLoading || localLoading;

  const validate = (): boolean => {
    const errs: typeof localErrors = {};
    // Validate URL
    const url = (rssUrl || '').trim();
    try {
      const u = new URL(url);
      if (!['http:', 'https:'].includes(u.protocol)) {
        errs.rssUrl = 'URL must start with http or https';
      }
    } catch {
      errs.rssUrl = 'Enter a valid RSS URL (e.g., https://example.com/feed)';
    }
    // Validate maxItems (optional)
    if (maxItems !== '' && (isNaN(Number(maxItems)) || !Number.isInteger(Number(maxItems)) || Number(maxItems) < 1 || Number(maxItems) > 100)) {
      errs.maxItems = 'Max items must be an integer between 1 and 100';
    }
    setLocalErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleRun = async () => {
    if (!validate()) return;
    try {
      setLocalLoading(true);
      const resp = await importArticlesFromRss(websitePK, rssUrl.trim(), {
        maxItems: maxItems === '' ? undefined : Number(maxItems),
        onlyNew,
        dryRun,
      });
      setResults(resp);
      setShowDialog(true);

      if (!dryRun) {
        const created = (typeof resp?.createdCount === 'number' ? resp.createdCount : 0) || (Array.isArray(resp?.createdIds) ? resp.createdIds.length : 0);
        if (created > 0) {
          // Refresh only when new items were created, and scope by websitePK
          dispatch(fetchFilteredArticles({ websitePK }));
        }
      }
    } catch (e: any) {
      setResults({ message: e?.response?.data?.message || e?.message || 'RSS import failed' });
      setShowDialog(true);
    } finally {
      setLocalLoading(false);
    }
  };

  const items = useMemo(() => {
    const r = results || {} as any;
    return (
      r.importedArticles || r.articles || r.previews || r.items || []
    ) as any[];
  }, [results]);

  const computedCount = useMemo(() => {
    if (!results) return 0;
    return results.count ?? items.length ?? 0;
  }, [results, items.length]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        RSS Feed Configuration
      </Typography>
      
      <TextField
        fullWidth
        label="RSS Feed URL"
        name="rssUrl"
        value={rssUrl || ''}
        onChange={onChange}
        error={!!(error || localErrors.rssUrl)}
        helperText={error || localErrors.rssUrl || "Enter the full URL of the RSS feed (e.g., https://www.example.com/feed/)"}
        disabled={isBusy}
        sx={{ mt: 1 }}
        placeholder="https://example.com/feed/"
      />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Max items (optional)"
            inputProps={{ min: 1, max: 100 }}
            value={maxItems}
            onChange={(e) => setMaxItems(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={isBusy}
            error={!!localErrors.maxItems}
            helperText={localErrors.maxItems || 'Limit how many items to process'}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Checkbox checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} disabled={isBusy} />}
            label="Only new items"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={isBusy} />}
            label="Preview only (no save)"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRun}
          disabled={isBusy}
          startIcon={localLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {dryRun ? 'Preview from RSS' : 'Import from RSS'}
        </Button>
      </Box>

      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>RSS Import Results</DialogTitle>
        <DialogContent dividers>
          {results && (
            <>
              {/* Feed summary */}
              {(results.feed?.title || results.feed?.link || typeof results.feed?.itemsCount === 'number') && (
                <Box sx={{ mb: 2 }}>
                  {results.feed?.title && (
                    <Typography variant="h6" gutterBottom>{results.feed.title}</Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    {results.feed?.link && (
                      <Chip component={Link as any} clickable href={results.feed.link} target="_blank" rel="noopener noreferrer" color="info" variant="outlined" label="Visit site" />
                    )}
                    {typeof results.feed?.itemsCount === 'number' && (
                      <Chip label={`${results.feed.itemsCount} feed items`} variant="outlined" />
                    )}
                    {results.websiteId && (
                      <Chip label={`Website: ${results.websiteId}`} variant="outlined" />
                    )}
                  </Stack>
                </Box>
              )}

              {/* Result chips */}
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {typeof computedCount === 'number' && (
                  <Chip icon={<ArticleIcon />} label={`${computedCount} item${computedCount === 1 ? '' : 's'}`} color="primary" variant="outlined" />
                )}
                {typeof results.durationSeconds === 'number' && (
                  <Chip label={`${results.durationSeconds.toFixed(2)}s`} color="secondary" variant="outlined" />
                )}
                <Chip icon={<CheckIcon />} label={dryRun ? 'Preview' : 'Imported'} color={dryRun ? 'info' as any : 'success'} variant="outlined" />
                {typeof results.createdCount === 'number' && (
                  <Chip label={`Created: ${results.createdCount}`} color="success" variant="outlined" />
                )}
                {typeof results.existingCount === 'number' && (
                  <Chip label={`Existing: ${results.existingCount}`} color="default" variant="outlined" />
                )}
                {typeof results.errorCount === 'number' && (
                  <Chip label={`Errors: ${results.errorCount}`} color="error" variant="outlined" />
                )}
              </Stack>

              {results.message && (
                <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">{results.message}</Typography>
              )}

              {/* Errors block */}
              {Array.isArray(results.errors) && results.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {results.errors.map((err: any, idx: number) => (
                    <Alert key={idx} severity="error" sx={{ mb: 1 }}>
                      {typeof err === 'string' ? err : err?.message || 'Unknown error'}
                    </Alert>
                  ))}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Preview links/authors list */}
              {Array.isArray(results.previewLinks) && results.previewLinks.length > 0 ? (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {results.previewLinks.map((linkUrl: string, idx: number) => {
                    const author = results.previewAuthors?.[idx];
                    return (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <Link href={linkUrl} target="_blank" rel="noopener noreferrer">{linkUrl}</Link>
                        </Typography>
                        {author && (
                          <Typography variant="caption" color="text.secondary">{author}</Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                // Fallback to generic list if items available from other fields
                <>
                  {items.length > 0 ? (
                    <Stack spacing={1}>
                      {items.slice(0, 50).map((item: any, idx: number) => {
                        const title = item?.title || item?.headline || 'Untitled';
                        const url = item?.link || item?.url || item?.originalUrl;
                        const date = item?.publishDate || item?.pubDate || item?.publishedAt;
                        return (
                          <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {url ? (
                                <Link href={url} target="_blank" rel="noopener noreferrer">{title}</Link>
                              ) : (
                                title
                              )}
                            </Typography>
                            {date && (
                              <Typography variant="caption" color="text.secondary">{new Date(date).toLocaleString()}</Typography>
                            )}
                          </Paper>
                        );
                      })}
                      {items.length > 50 && (
                        <Typography variant="caption" color="text.secondary">Showing first 50 of {items.length} items…</Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No items returned.</Typography>
                  )}
                </>
              )}

              {/* IDs details when not a dry run */}
              {!dryRun && (Array.isArray(results.createdIds) || Array.isArray(results.existingIds)) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  {Array.isArray(results.createdIds) && results.createdIds.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Created IDs ({results.createdIds.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={0.5}>
                          {results.createdIds.slice(0, 100).map((id: string, i: number) => (
                            <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace' }}>{id}</Typography>
                          ))}
                          {results.createdIds.length > 100 && (
                            <Typography variant="caption" color="text.secondary">Showing first 100 of {results.createdIds.length}…</Typography>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  {Array.isArray(results.existingIds) && results.existingIds.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Existing IDs ({results.existingIds.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={0.5}>
                          {results.existingIds.slice(0, 100).map((id: string, i: number) => (
                            <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace' }}>{id}</Typography>
                          ))}
                          {results.existingIds.length > 100 && (
                            <Typography variant="caption" color="text.secondary">Showing first 100 of {results.existingIds.length}…</Typography>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};